import { z } from 'zod';
/**
 * API-Verträge.
 *
 * **Die einzige Quelle für die Gestalt der Schnittstellen.** Backend, Web-,
 * Partner- und Verwaltungsoberfläche sowie der Electron-Klient benutzen
 * dieselben Schemata; `TECHNOLOGY.md` §5 lässt doppelte Typdefinitionen
 * ohnehin nicht zu.
 *
 * Der Grund ist nicht Bequemlichkeit: Bisher standen die Anfrageschemata als
 * `z.object(…)` in den Routen und die Antworttypen ein zweites Mal von Hand in
 * `apps/web/src/api/`. Beide beschrieben dasselbe, ohne voneinander zu wissen —
 * ändert sich eines, fällt das erst zur Laufzeit auf.
 *
 * Aus denselben Schemata entsteht später die Dokumentation der Partner-API
 * (`Projektstruktur.md` §7). Deshalb: keine serverinternen Schemata hier.
 * Umgebungsvariablen und Datenbankzeilen sind keine Verträge und bleiben, wo
 * sie sind.
 *
 * **Zod bleibt im Handler**, nicht als Middleware (`TECHNOLOGY.md` §5,
 * ADR-0046). Hier steht nur, *was* geprüft wird — nicht *wo*.
 */
// ---------------------------------------------------------------------------
// Gemeinsames
// ---------------------------------------------------------------------------
/**
 * Standard-Fehlerformat nach `SECURITY.md` §2.
 *
 * Keine Stacktraces, keine internen Pfade, keine Umgebungsdetails. Was der
 * Aufrufer bekommt, ist eine Meldung, ein Code zum Auswerten und der Status.
 */
export const fehlerSchema = z.object({
    error: z.string(),
    code: z.string(),
    status: z.number().int(),
});
// ---------------------------------------------------------------------------
// Gerätekopplung (ADR-0004)
// ---------------------------------------------------------------------------
/**
 * Einlösen eines Kopplungscodes.
 *
 * Bewusst nach Whitelist: Unbekannte Felder werden verworfen, nicht
 * übernommen. Die Obergrenzen sind keine Schönheit, sondern verhindern, dass
 * ein Aufrufer beliebig große Werte in die Verarbeitung schiebt.
 */
export const kopplungEinloesenSchema = z.object({
    code: z.string().min(1).max(64),
    fingerprint: z.string().min(8).max(512),
    label: z.string().min(1).max(64).optional(),
    clientVersion: z.string().max(32).optional(),
});
/** Antwort darauf. Das Geheimnis verlässt den Server **genau einmal**. */
export const kopplungErgebnisSchema = z.object({
    deviceId: z.string(),
    secret: z.string(),
});
/**
 * Der Support-Kontakt aus Sicht des Kunden — die Fußzeile der Kundenanwendung.
 *
 * Drei Angaben, mehr nicht: Firma, Telefon, E-Mail. Keine Anschrift, keine
 * Website, keine Impressumsfelder.
 */
export const supportKontaktSchema = z.object({
    name: z.string(),
    telefon: z.string(),
    email: z.string(),
});
/** `GET /api/me` — wer ist dieses Gerät, und was darf es. */
export const eigeneDatenSchema = z.object({
    appUserId: z.string(),
    deviceId: z.string(),
    permissions: z.array(z.string()),
    /**
     * Wer diesem Kunden hilft — für den Menüpunkt „<Firma> anrufen" der App.
     * `null`, solange der Partner nichts hinterlegt hat.
     */
    partner: supportKontaktSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Sitzungsübergabe an den Browser (ADR-0004)
// ---------------------------------------------------------------------------
/**
 * Einlösen des Einmal-Tokens.
 *
 * Die Untergrenze von 20 Zeichen ist Absicht: Ein kurzer Wert kann kein
 * Token aus dieser Ausgabe sein und wird abgewiesen, bevor die Datenbank
 * überhaupt befragt wird.
 */
export const uebergabeEinloesenSchema = z.object({
    token: z.string().min(20).max(128),
});
/** `POST /api/session/handoff` — Token und Adresse für den Browser. */
export const uebergabeSchema = z.object({
    url: z.string(),
    expiresAt: z.string(),
});
/**
 * Funktionen und Editionen (ADR-0014).
 *
 * **Funktionen** sind die Schalter, die die Software kennt — jede steht fuer
 * etwas, das ein Endpunkt prueft. Neue Funktionen kommen mit Releases.
 * **Editionen** sind Daten: ein Schluessel, ein Name und die Menge der
 * Funktionen, die netable in der Verwaltung zusammenklickt. Der Partner
 * verkauft nur, was netable in seinen Katalog gelegt hat; abgerechnet wird
 * Preis je Seed und Monat.
 */
export const funktionen = ['click_to_dial', 'anrufverlauf', 'kontakte', 'anrufauswertung'];
export const FUNKTIONSNAMEN = {
    click_to_dial: {
        name: 'Click-to-Dial',
        beschreibung: 'Rufnummern aus jeder Anwendung waehlen — Desktop-App und Browser.',
    },
    anrufverlauf: {
        name: 'Einfacher Anrufverlauf',
        beschreibung: 'Die eigenen Anrufe mit Ausgang und Dauer.',
    },
    kontakte: {
        name: 'Kontaktdatenbank',
        beschreibung: 'Eigene und zentrale Kontakte, Namen zu Rufnummern.',
    },
    anrufauswertung: {
        name: 'Anrufauswertung',
        beschreibung: 'Erreichbarkeit, Wartezeiten, Stosszeiten je Anlage, Channel und Mitarbeiter — alle Anrufe des Kontos werden gespeichert.',
    },
};
/** Schluessel einer Edition: klein, stabil, nie umbenannt — der Name ist aenderbar. */
export const editionSchluessel = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9][a-z0-9_-]{1,39}$/, 'Kein gueltiger Editionsschluessel');
/**
 * Eine Edition, wie der Partner-Stack sie kennt — aus dem Katalog, den die
 * Verwaltung verteilt. `seedCent` ist der Preis dieses Partners je Seed und
 * Monat (netto), `null` ohne Preisliste. `eolAm` ist das Ende (YYYY-MM-DD):
 * Ab dann laesst sie sich keinem Kunden mehr zuweisen.
 */
export const partnerEditionSchema = z.object({
    key: editionSchluessel,
    name: z.string(),
    funktionen: z.array(z.enum(funktionen)),
    eolAm: z.string().nullable(),
    /** Preis dieses Partners je Seed und Monat bei jaehrlicher Laufzeit, netto, laufender Monat; `null` ohne VK. */
    seedCent: z.number().int().nonnegative().nullable(),
    /** Preis je Seed und Monat bei monatlicher Laufzeit — vom Server gerechnet (`monatspreisCent`); `null` ohne VK. */
    seedMonatlichCent: z.number().int().nonnegative().nullable(),
    /** Mindestlaufzeit in Monaten fuer neue Lizenzen — Vorgabe der Edition oder Abweichung des Partners. */
    mindestlaufzeitMonate: z.number().int().positive(),
    /** Jahresrabatt in Prozent: Jaehrlich zahlt der Partner den VK, monatlich den VK geteilt durch (1 − Rabatt). */
    monatlichAufschlagProzent: z.number().int().nonnegative(),
    /** Steht sie im Katalog dieses Partners — sonst nur noch fuer Bestandskunden. */
    imKatalog: z.boolean(),
    /** Wie viele Kunden dieses Partners sie gerade haben. */
    kunden: z.number().int().nonnegative(),
});
export const partnerEditionenSchema = z.object({
    editionen: z.array(partnerEditionSchema),
    /** Wann die Verwaltung den Katalog zuletzt verteilt hat; `null` = noch nie. */
    stand: z.string().nullable(),
});
/**
 * Lizenzen (ADR-0015): Der Partner kauft je Kunde eine Edition. Bezahlt wird
 * dabei nichts — der Kauf setzt das Startdatum der Laufzeit, legt die
 * erwartete Mindestmenge an Seeds fest und dokumentiert netable einen
 * weiteren Kunden. Zwei Laufzeiten: `mindest` (Mindestlaufzeit, Vorgabe zwoelf
 * Monate, verlaengert sich danach um je zwoelf Monate) und `monatlich`
 * (Aufschlag je Edition, Seeds zum Monatsende kuendbar).
 */
export const lizenzLaufzeiten = ['mindest', 'monatlich'];
export const lizenzSchema = z.object({
    edition: editionSchluessel,
    laufzeit: z.enum(lizenzLaufzeiten),
    /** Bei `mindest`: die Mindestlaufzeit in Monaten, wie beim Kauf vereinbart. */
    mindestlaufzeitMonate: z.number().int().positive(),
    /** Erwartete Seeds — wird waehrend der Laufzeit nicht unterschritten. */
    mindestmenge: z.number().int().nonnegative(),
    /** Kaufdatum, `YYYY-MM-DD`. */
    gekauftAmTag: z.string(),
    /**
     * Ab wann bezahlt wird — immer ein Monatserster (ADR-0015 Nachtrag): Kauf bis
     * zum 10. → der naechste Monatserste, danach der uebernaechste. Bis dahin
     * laeuft der Testzeitraum; die Laufzeit beginnt an diesem Tag.
     */
    bezahltAb: z.string(),
    /** Beginn der Laufzeit, `YYYY-MM-DD` — gleich `bezahltAb`. */
    start: z.string(),
    /** Ende der laufenden Laufzeit, `YYYY-MM-DD`; bei `monatlich` das Monatsende. */
    laufzeitEnde: z.string(),
    gekauftAm: z.string(),
    gekauftVon: z.string().nullable(),
});
/** `POST /partner/api/kunden/:host/lizenz` — Edition kaufen oder wechseln. */
export const lizenzKaufenSchema = z.object({
    edition: editionSchluessel,
    laufzeit: z.enum(lizenzLaufzeiten),
    mindestmenge: z.number().int().nonnegative().max(100_000),
});
/** Warum ein Kauf waehrend einer Mindestlaufzeit nicht geht — der Server entscheidet. */
export const lizenzAbstiege = ['edition', 'laufzeit', 'menge'];
/**
 * `POST /partner/api/kunden/:host/lizenz/vorschau` — was ein Kauf ergaebe,
 * gerechnet im Server mit denselben Regeln wie der Kauf selbst: Preis mit
 * Aufschlag, Testzeitraum, Zahlungsbeginn, Laufzeitende, und ob der Kauf
 * angesichts einer laufenden Lizenz ueberhaupt erlaubt ist. Die Oberflaeche
 * zeigt nur an; sie rechnet nichts.
 */
export const lizenzVorschauSchema = z.object({
    edition: editionSchluessel,
    editionName: z.string(),
    laufzeit: z.enum(lizenzLaufzeiten),
    mindestlaufzeitMonate: z.number().int().positive(),
    mindestmenge: z.number().int().nonnegative(),
    /** Preis je Seed und Monat in Cent — jaehrlich der VK, monatlich der Monatspreis (`monatspreisCent`); `null` ohne VK. */
    preisJeSeedCent: z.number().int().nonnegative().nullable(),
    /** Jahresrabatt in Prozent — so viel spart die jaehrliche Laufzeit gegenueber der monatlichen. */
    monatlichAufschlagProzent: z.number().int().nonnegative(),
    /** Mindestbetrag je Monat in Cent — Mindestmenge mal Preis; `null` ohne VK. */
    gesamtJeMonatCent: z.number().int().nonnegative().nullable(),
    /** Erster bezahlter Tag — bis dahin Testzeitraum. Bei einer fortgesetzten Lizenz deren Zahlungsbeginn. */
    bezahltAb: z.string(),
    /** Ob der Testzeitraum noch vor uns liegt (sonst wird die laufende Lizenz fortgesetzt). */
    imTestzeitraum: z.boolean(),
    start: z.string(),
    laufzeitEnde: z.string(),
    /** Die Lizenz, die der Kunde heute hat — mit ihrem heute gueltigen Ende. */
    bisher: z
        .object({
        edition: editionSchluessel,
        editionName: z.string(),
        laufzeit: z.enum(lizenzLaufzeiten),
        mindestmenge: z.number().int().nonnegative(),
        laufzeitEnde: z.string(),
    })
        .nullable(),
    /** `null`: der Kauf ginge. Sonst der erste Grund, der ihn sperrt (`409 laufzeit_laeuft`). */
    abstieg: z.enum(lizenzAbstiege).nullable(),
});
/**
 * Ab wann ein Kauf bezahlt wird: Kauf bis einschliesslich zum 10. → der erste
 * Tag des naechsten Monats; spaeter → der erste Tag des uebernaechsten. Der
 * Zeitraum davor ist der Testzeitraum. Reine Funktion, `YYYY-MM-DD` hinein und heraus.
 */
/**
 * Monatspreis aus dem VK (Inhaber, 14.09.2026): Der eingegebene Preis ist der
 * Preis bei **jaehrlicher** Laufzeit; wer monatlich kuendbar kauft, zahlt so
 * viel, dass „jaehrlich −N %" exakt stimmt — VK geteilt durch (1 − N/100),
 * kaufmaennisch gerundet. 9,00 € bei 15 % → 10,59 €. Das Feld heisst aus
 * Bestandsgruenden weiter `monatlichAufschlagProzent`; gemeint ist der
 * Jahresrabatt. Rechnet ausschliesslich der Server.
 */
export function monatspreisCent(vkCent, jahresrabattProzent) {
    if (jahresrabattProzent >= 100)
        throw new Error(`Jahresrabatt ${jahresrabattProzent} % ergibt keinen Monatspreis`);
    return Math.round(vkCent / (1 - jahresrabattProzent / 100));
}
export function bezahltAbVon(kaufTag) {
    const [jahr, monat, tag] = kaufTag.split('-').map(Number);
    if (!jahr || !monat || !tag)
        throw new Error(`Kein Datum: ${kaufTag}`);
    const d = new Date(Date.UTC(jahr, monat - 1 + (tag <= 10 ? 1 : 2), 1));
    return d.toISOString().slice(0, 10);
}
/** Ob eine Edition heute verkauft werden darf: im Katalog und nicht am Ende. */
export function editionVerkaufbar(e, heute = new Date()) {
    if (!e.imKatalog)
        return false;
    if (e.eolAm === null)
        return true;
    return e.eolAm > heute.toISOString().slice(0, 10);
}
/** `GET /api/session/me` — wer ist in dieser Browsersitzung angemeldet. */
export const sitzungSchema = z.object({
    appUserId: z.string(),
    /** Wie die Person angesprochen wird — der Name, den der Partner eingetragen hat. */
    anzeigename: z.string(),
    permissions: z.array(z.string()),
    /**
     * Die Edition des Unternehmens. Die Kundenanwendung zeigt daran, was der
     * Vertrag umfasst — etwa ob Kontakte dazugehören. Freigeschaltet wird
     * nichts im Browser: Was die Edition nicht trägt, hat auch keinen Endpunkt.
     */
    edition: editionSchluessel,
    editionName: z.string(),
    /** Die Funktionen der Edition (ADR-0014) — die Kundenanwendung zeigt danach; durchgesetzt wird am Endpunkt. */
    funktionen: z.array(z.enum(funktionen)),
    /**
     * Wer diesem Kunden hilft. `null`, solange der Partner nichts hinterlegt
     * hat — dann zeigt die Kundenanwendung keine Fußzeile.
     */
    partner: supportKontaktSchema.nullable(),
    /**
     * Zweiter Faktor (ADR-0004 Nachtrag 6). `pflicht` ist wahr fuer
     * Selbstverwalter — ohne ihn kommen sie nicht ueber das Passwort herein.
     * Durchgesetzt wird an der Anmeldung, nicht hier.
     */
    zweiterFaktor: z.object({
        eingerichtet: z.boolean(),
        pflicht: z.boolean(),
        /**
         * Die Pflicht kommt vom Partner und gilt fuer alle Anwender dieses Kunden
         * (ADR-0004 Nachtrag 7). Dann laesst der Faktor sich nicht abschalten.
         */
        vomPartner: z.boolean(),
    }),
});
// ---------------------------------------------------------------------------
// sipgate-Zugang
// ---------------------------------------------------------------------------
/**
 * Hinterlegen von Token-ID und Token.
 *
 * Der Token wird nach dem Speichern **nie wieder ausgeliefert** (Briefing
 * 5.6) — deshalb gibt es dazu kein Lese-Schema, nur die Angaben unten.
 */
export const sipgateZugangSetzenSchema = z.object({
    tokenId: z.string().min(1).max(128),
    token: z.string().min(8).max(512),
});
/** Was über den hinterlegten Zugang gesagt werden darf. */
export const sipgateZugangSchema = z.object({
    hinterlegt: z.boolean(),
    /** Die Token-ID ist kein Geheimnis und darf angezeigt werden. */
    tokenId: z.string().nullable(),
    /** Nur die letzten vier Zeichen des Tokens. */
    tokenLast4: z.string().nullable(),
    status: z.enum(['active', 'invalid']).nullable(),
    zuletztGeprueft: z.string().nullable(),
    ausgewaehltesGeraet: z.string().nullable(),
    ausgewaehltesGeraetName: z.string().nullable(),
});
/**
 * Ein wählbares sipgate-Gerät.
 *
 * Geräte vom Typ `EXTERNAL` kommen hier nicht vor — darauf lässt sich kein
 * Anruf starten (Briefing 5.4), und sie anzubieten führt dazu, dass der
 * Anwender eines auswählt und das Wählen später ohne erkennbaren Grund
 * scheitert.
 */
export const sipgateGeraetSchema = z.object({
    id: z.string(),
    name: z.string(),
    art: z.string(),
    online: z.boolean(),
});
/** Auswahl des Geräts. Nur nötig, wenn es mehr als eines gibt. */
export const sipgateGeraetWaehlenSchema = z.object({
    deviceId: z.string().min(1).max(64),
});
/**
 * Antwort auf das Hinterlegen des Zugangs.
 *
 * `auswahlNoetig` ist `false`, wenn genau ein Gerät gefunden wurde — dann hat
 * der Dienst es bereits gesetzt. Eine Auswahlliste mit einem einzigen Eintrag
 * ist keine Wahl, sondern eine Rückfrage ohne Inhalt.
 */
export const sipgateZugangErgebnisSchema = z.object({
    zugang: sipgateZugangSchema,
    geraete: z.array(sipgateGeraetSchema),
    auswahlNoetig: z.boolean(),
});
// ---------------------------------------------------------------------------
// Wählen
// ---------------------------------------------------------------------------
/**
 * Wählauftrag.
 *
 * Die Nummer wird serverseitig **erneut** normalisiert und gegen die
 * Sperrliste geprüft. Der Klient läuft auf dem Rechner des Anwenders und ist
 * keine Instanz, der man das Einhalten von Regeln überlässt.
 */
export const waehlenSchema = z.object({
    nummer: z.string().min(3).max(64),
});
/**
 * Antwort auf einen angenommenen Wählauftrag.
 *
 * `weg` sagt, welcher der beiden sipgate-Endpunkte getragen hat — bei einer
 * Störung ist das die erste Frage, und der Wert steht sonst nirgends.
 */
export const waehlErgebnisSchema = z.object({
    kennung: z.string().nullable(),
    weg: z.enum(['neo', 'classic']),
    gewaehlt: z.string(),
    /** Unsere Kennung des Anrufs — der Verlauf haengt daran, nicht an sipgate. */
    anrufId: z.string(),
});
// ---------------------------------------------------------------------------
// Verlauf und Anrufereignisse (Push-API, ADR-0011)
// ---------------------------------------------------------------------------
/** Zustaende eines Anrufs, wie der Dienst sie aus den Push-Events ableitet. */
export const anrufZustaende = [
    'requested',
    'dialing',
    'ringing',
    'answered',
    'ended',
    'failed',
    'unknown_end',
];
export const verlaufEintragSchema = z.object({
    id: z.string(),
    nummer: z.string(),
    zustand: z.enum(anrufZustaende),
    /** `normalClearing`, `busy`, `cancel`, `noAnswer`, … — nur nach `hangup`. */
    grund: z.string().nullable(),
    angefragt: z.string(),
    angenommen: z.string().nullable(),
    beendet: z.string().nullable(),
    dauerSekunden: z.number().int().nonnegative().nullable(),
});
export const verlaufSchema = z.object({ eintraege: z.array(verlaufEintragSchema) });
/**
 * `GET /api/calls/:id` — der Zustand eines eigenen Anrufs, wie ihn die
 * Push-Events gesetzt haben. Aus der Datenbank, ohne Runde zu sipgate: Die
 * App fragt ihn im Sekundentakt ab, solange die Anzeige steht.
 */
export const anrufZustandSchema = z.object({
    id: z.string(),
    zustand: z.enum(anrufZustaende),
    grund: z.string().nullable(),
    klingeltSeit: z.string().nullable(),
    angenommenSeit: z.string().nullable(),
    beendetAm: z.string().nullable(),
});
/**
 * Push-Adresse eines Kunden — Sicht des Partners.
 *
 * Die Adresse traegt das Geheimnis des Kunden; ein sipgate-Administrator
 * traegt sie in der sipgate-Web-App als URL fuer ein- und ausgehende Anrufe
 * ein. `null`, solange noch keine erzeugt wurde.
 */
export const partnerPushSchema = z.object({
    eingerichtet: z.boolean(),
    adresse: z.string().nullable(),
});
/**
 * Laufende Gespräche.
 *
 * **Klingelnde Anrufe sind nicht enthalten** — sipgate liefert nur bestehende
 * Verbindungen. Eine leere Liste heißt deshalb nicht „beendet", sondern
 * „gerade nicht verbunden".
 */
export const laufenderAnrufSchema = z.object({
    kennung: z.string(),
    gegenstelle: z.string().nullable(),
    gehalten: z.boolean().optional(),
});
export const laufendeAnrufeSchema = z.object({
    anrufe: z.array(laufenderAnrufSchema),
    /** `true`, wenn sipgate gerade nicht antwortet — die Anzeige bleibt dann stehen. */
    stoerung: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// Partner-Oberfläche (Projektstruktur.md §3, §7)
// ---------------------------------------------------------------------------
/**
 * Zustände eines Kunden im Stack.
 *
 * Steht hier und nicht nur im Datenbankschema, weil die Oberfläche sie
 * anzeigt und die Partner-API sie ausliefert. Eine zweite Aufzählung an einer
 * dieser Stellen wäre die zweite Wahrheit, vor der `contracts.ts` steht.
 */
export const kundeZustaende = ['bereitstellung', 'aktiv', 'deaktiviert', 'geloescht'];
/** Stufen der Selbstverwaltung, vom Partner je Kunde geschaltet (§3.1). */
export const adminLightStufen = ['keine', 'geraete', 'anwender_und_geraete'];
/**
 * Ein Kunde in der Sicht des Partners.
 *
 * **Verwaltungsdaten, keine Gesprächsdaten** (`Projektstruktur.md` §3). Was
 * hier nicht steht, sieht der Partner auch nicht: keine Rufnummern, kein
 * Verlauf, keine Token oder deren Metadaten, keine Zusammenfassungen. Die
 * Aufzählung ist der Vertrag — ein Feld, das der Server zusätzlich schickt,
 * ist ein Fehler, den `TESTING.md` §3.12 zur Laufzeit fängt.
 */
export const partnerKundeSchema = z.object({
    id: z.string().uuid(),
    host: z.string(),
    anzeigename: z.string(),
    zustand: z.enum(kundeZustaende),
    edition: editionSchluessel,
    /** `null` heißt: kein Cap. Der Partner darf ihn weglassen (§4). */
    seatCap: z.number().int().nonnegative().nullable(),
    /**
     * Belegte Seeds — aktive Anwender **mit sipgate-Verknuepfung**. Ein
     * angelegter Anwender ohne Token zaehlt nicht (Entscheidung des Inhabers
     * vom 13.09.2026): Er verbraucht nichts, solange er nicht telefonieren kann.
     *
     * `null`, wenn die Kundendatenbank gerade nicht erreichbar ist. Bewusst
     * nicht `0`: Eine Null sähe aus wie ein leerer Kunde, und der Partner
     * träfe auf dieser Grundlage Entscheidungen über Seeds.
     */
    seatsBelegt: z.number().int().nonnegative().nullable(),
    /**
     * Kennzahlen aus der Kundendatenbank. Alle `null`, wenn sie gerade
     * nicht erreichbar ist — aus demselben Grund wie bei `seatsBelegt`:
     * Eine Null sähe aus wie ein Befund.
     */
    geraete: z.number().int().nonnegative().nullable(),
    /** Anwender ohne hinterlegten sipgate-Zugang — die können nicht telefonieren. */
    anwenderOhneZugang: z.number().int().nonnegative().nullable(),
    /** Geräte ohne Lebenszeichen seit dreißig Tagen (`Projektstruktur.md` §3.2). */
    geraeteOhneZeichen: z.number().int().nonnegative().nullable(),
    adminLightStufe: z.enum(adminLightStufen),
    /**
     * Der Partner verlangt den zweiten Faktor fuer **alle** Anwender dieses
     * Kunden (ADR-0004 Nachtrag 7). Dann kann ihn beim Kunden niemand
     * abschalten — auch der Selbstverwalter nicht.
     */
    zweiFaktorPflicht: z.boolean(),
    geraeteJeAnwender: z.number().int().positive(),
    istDemo: z.boolean(),
    angelegtAm: z.string(),
    aktivSeit: z.string().nullable(),
    /** Die gekaufte Lizenz (ADR-0015); `null`, solange der Partner keine gekauft hat. */
    lizenz: lizenzSchema.nullable(),
});
export const partnerKundenSchema = z.object({
    kunden: z.array(partnerKundeSchema),
});
/**
 * Rollen und Rechte der Partner-Oberfläche.
 *
 * **Rollen sind Daten** (SECURITY.md §4): Sie stehen in der Registry, und der
 * Vertrag kennt sie nur als Schlüssel. Welche es gibt und was sie tragen,
 * sagt der Server mit der Kontenliste (`partnerRollenrechtSchema`). Rechte
 * dagegen sind Code — ein Recht ist, was ein Endpunkt prüft — und stehen
 * deshalb hier als Liste.
 */
export const partnerRolleSchema = z.string().trim().min(1).max(64);
export const partnerRechte = [
    'kunden.sehen',
    'kunden.technik',
    'kunden.kaufmaennisch',
    'kunden.anlegen',
    'konten.verwalten',
    'partner.profil',
];
// ---------------------------------------------------------------------------
// Unternehmens- und Supportdaten des Partners
// ---------------------------------------------------------------------------
/**
 * Was der Partner über sich pflegt.
 *
 * Zwei Blöcke: das Unternehmen (Impressumsdaten) und der Support-Kontakt —
 * das, was ein Kunde sieht, wenn er Hilfe braucht. Leere Felder sind
 * erlaubt: Ein Partner ohne Website hat keine; ein Pflichtfeld erzwänge dort
 * einen Platzhalter.
 */
const feld = z.string().trim().max(200);
const emailOderLeer = z.union([z.string().trim().email().max(200), z.literal('')]);
/**
 * Nur http(s): `z.url()` allein liesse `javascript:` durch — und der Wert
 * wird in der Kundenanwendung als Link ausgegeben, bei jedem Kunden.
 */
const webOderLeer = z.union([
    z
        .string()
        .trim()
        .max(200)
        .url()
        .refine((wert) => /^https?:\/\//i.test(wert), { message: 'Nur http(s)-Adressen' }),
    z.literal(''),
]);
export const partnerProfilAendernSchema = z.object({
    unternehmen: z.object({
        name: feld,
        strasse: feld,
        plz: feld.max(20),
        ort: feld,
        telefon: feld.max(60),
        email: emailOderLeer,
        web: webOderLeer,
    }),
    support: z.object({
        telefon: feld.max(60),
        email: emailOderLeer,
    }),
});
export const partnerProfilSchema = partnerProfilAendernSchema.extend({
    /** `null`, solange noch nie gespeichert wurde. */
    aktualisiertAm: z.string().nullable(),
});
/** Wer gerade in der Partner-Oberfläche angemeldet ist. */
export const partnerSitzungSchema = z.object({
    anzeigename: z.string(),
    email: z.string(),
    rolle: partnerRolleSchema,
    /**
     * Was diese Rolle darf — abgeleitet, nicht gespeichert.
     *
     * Die Oberfläche blendet daran aus, was nicht geht. Das ist Bequemlichkeit
     * und kein Schutz: Durchgesetzt wird jedes Recht am Endpunkt.
     */
    rechte: z.array(z.enum(partnerRechte)),
});
/**
 * Ein Anwender in der Sicht des Partners.
 *
 * **Kein Wort über den sipgate-Token außer, ob einer da ist.** Keine
 * Token-ID, keine letzten vier Zeichen, kein Prüfzeitpunkt: Der Partner soll
 * sehen, ob jemand telefonieren kann, und sonst nichts über fremde
 * Zugangsdaten (`Projektstruktur.md` §3.1 — die Token-Grenze gilt auf jeder
 * Stufe).
 */
export const partnerAnwenderSchema = z.object({
    id: z.string().uuid(),
    anzeigename: z.string(),
    /**
     * Kennung fuer die Anmeldung im Browser (ADR-0004 Nachtrag 5); `null` heisst:
     * Dieser Anwender kommt nur ueber die App herein.
     */
    email: z.string().email().nullable(),
    /** Ob schon ein Passwort gesetzt ist — nicht welches. */
    hatPasswort: z.boolean(),
    aktiv: z.boolean(),
    geraete: z.number().int().nonnegative(),
    /** Nur das Ob. Siehe oben. */
    sipgateZugang: z.boolean(),
    /**
     * Darf diese Person beim Kunden selbst verwalten?
     *
     * Die **Vergabe**, nicht die Wirkung: Wie weit sie reicht, entscheidet die
     * Stufe am Kunden (`adminLightStufe`). Bei `keine` trägt die Zuweisung
     * nichts — sie bleibt aber stehen, damit ein Anheben der Stufe nicht jede
     * Person einzeln neu vergeben lässt (`Projektstruktur.md` §3.1).
     */
    selbstverwaltung: z.boolean(),
    /** Darf diese Person die **zentrale** Kontaktdatenbank pflegen (ADR-0016)? */
    kontaktpflege: z.boolean(),
    angelegtAm: z.string(),
});
/** Was sich an einem Anwender vom Partner aus ändern lässt — bislang eines. */
/**
 * Änderung an einem Anwender — Selbstverwaltung vergeben oder das Konto
 * sperren. Mindestens eines von beiden; ein leerer Rumpf ist ein Fehler,
 * keine Handlung.
 */
export const anwenderEmail = z.string().trim().toLowerCase().email().max(200);
export const partnerAnwenderAendernSchema = z
    .object({
    selbstverwaltung: z.boolean().optional(),
    kontaktpflege: z.boolean().optional(),
    aktiv: z.boolean().optional(),
    /** `null` nimmt die Kennung wieder weg — dann geht nur noch der Weg ueber die App. */
    email: anwenderEmail.nullable().optional(),
})
    .refine((e) => e.selbstverwaltung !== undefined ||
    e.aktiv !== undefined ||
    e.email !== undefined ||
    e.kontaktpflege !== undefined, { message: 'Nichts zu ändern' });
/** Anwender anlegen — nur der Name; alles Weitere entsteht beim Koppeln. */
export const partnerAnwenderAnlegenSchema = z.object({
    anzeigename: z.string().trim().min(1).max(120),
    /** Ohne Adresse geht spaeter nur der Weg ueber die App. */
    email: anwenderEmail.optional(),
});
/**
 * Ein Kopplungscode — **einmalig** in der Antwort, danach nur noch als Hash
 * in der Datenbank (ADR-0004). Wer ihn verliert, erzeugt einen neuen.
 */
export const partnerKopplungscodeSchema = z.object({
    code: z.string(),
    gueltigBis: z.string(),
    einloesungen: z.number().int().positive(),
});
/**
 * Einmal-Link zum Setzen des Passworts — **einmalig** in der Antwort, danach
 * nur noch als Hash in der Datenbank. Es gibt keinen Mailversand; wer ihn
 * verliert, stellt einen neuen aus, und der entwertet den alten.
 */
export const partnerZugangslinkSchema = z.object({
    url: z.string().url(),
    gueltigBis: z.string(),
});
/** Untergrenze fuer das Passwort eines Anwenders. Laenge schlaegt Zeichenklassen. */
export const PASSWORT_MINDESTLAENGE = 12;
/** `POST /api/zugang/einloesen` — Link einloesen und Passwort setzen. */
export const zugangEinloesenSchema = z.object({
    token: z.string().min(10).max(500),
    passwort: z.string().min(PASSWORT_MINDESTLAENGE).max(200),
});
export const partnerAnwenderListeSchema = z.object({
    anwender: z.array(partnerAnwenderSchema),
});
/** Zustände eines Geräts. */
export const geraetZustaende = ['pending', 'active', 'revoked'];
/**
 * Ein Gerät in der Sicht des Partners.
 *
 * Die Kennung wird **gekürzt** ausgeliefert (`Projektstruktur.md` §7). Sie
 * reicht zum Wiedererkennen in einem Gespräch und taugt nicht als Kennung, mit
 * der sich anderswo etwas anfangen ließe. Fingerabdruck und Geheimnis
 * erscheinen nirgends — beide liegen ohnehin nur als Hash vor.
 */
export const partnerGeraetSchema = z.object({
    /** Erste acht Zeichen der Kennung, zum Wiedererkennen. */
    kennung: z.string(),
    bezeichnung: z.string(),
    anwender: z.string(),
    zustand: z.enum(geraetZustaende),
    gekoppeltAm: z.string().nullable(),
    zuletztGesehen: z.string().nullable(),
    /** `true`, wenn seit dreißig Tagen kein Lebenszeichen kam (§3.2). */
    ohneZeichen: z.boolean(),
});
export const partnerGeraetListeSchema = z.object({
    geraete: z.array(partnerGeraetSchema),
});
/**
 * Ein Konto der Partner-Oberfläche.
 *
 * **Kein Kennwort, kein Hash, keine Sitzungsdaten.** Das Kennwort erscheint
 * genau einmal, beim Anlegen und beim Zurücksetzen, und nirgends sonst — es
 * wird ausschließlich als Argon2id-Hash abgelegt. Ein Kennwort, das sich
 * später noch einmal abrufen lässt, ist keines.
 */
export const partnerKontoSchema = z.object({
    id: z.string().uuid(),
    email: z.string(),
    anzeigename: z.string(),
    /** `gesperrt` heißt: angemeldet bleiben geht nicht, der Eintrag bleibt. */
    gesperrt: z.boolean(),
    /** Rolle des Kontos (`Projektstruktur.md` §3.0). */
    rolle: partnerRolleSchema,
    /** Das eigene Konto — die Oberfläche darf sich nicht selbst aussperren lassen. */
    ichSelbst: z.boolean(),
    angelegtAm: z.string(),
    zuletztGesehen: z.string().nullable(),
});
/**
 * Was eine Rolle mitbringt — **vom Server, nicht aus der Oberflaeche**.
 *
 * Die Seite, auf der Rollen vergeben werden, muss zeigen koennen, was der Griff
 * bewirkt. Diese Zuordnung dort abzuschreiben hiesse, sie zweimal zu pflegen:
 * Verschiebt `apps/api/src/partner/rollen.ts` einen Schnitt, behauptete die
 * Oberflaeche weiter das Alte — und niemand merkte es, weil beides fuer sich
 * stimmig aussieht.
 *
 * Der Vertrag transportiert die Zuordnung, er legt sie nicht fest.
 */
export const partnerRollenrechtSchema = z.object({
    rolle: partnerRolleSchema,
    /** Anzeigename aus `partner_role` — der Server benennt, die Oberfläche zeigt. */
    name: z.string().min(1),
    rechte: z.array(z.enum(partnerRechte)),
});
export const partnerKontenSchema = z.object({
    konten: z.array(partnerKontoSchema),
    /** Der Rollenkatalog, damit die Auswahl erklaeren kann, was sie vergibt. */
    rollen: z.array(partnerRollenrechtSchema),
});
/** Was beim Anlegen eines Kontos hereinkommt. */
export const partnerKontoAnlegenSchema = z.object({
    email: z.string().trim().email().max(254),
    anzeigename: z.string().trim().min(1).max(120),
    rolle: partnerRolleSchema,
});
/**
 * Was sich an einem Konto ändern lässt.
 *
 * Beides ist wahlfrei, aber mindestens eines muss dabei sein — eine Änderung,
 * die nichts ändert, ist ein Programmierfehler und keine gültige Anfrage.
 */
export const partnerKontoAendernSchema = z
    .object({
    gesperrt: z.boolean().optional(),
    rolle: partnerRolleSchema.optional(),
})
    .refine((wert) => wert.gesperrt !== undefined || wert.rolle !== undefined, {
    message: 'Mindestens eines von gesperrt oder rolle muss angegeben sein',
});
/**
 * Antwort auf „angelegt" und „Kennwort zurückgesetzt".
 *
 * Das Kennwort steht hier **einmalig** im Klartext. Wer es nicht weitergibt,
 * muss ein neues erzeugen.
 */
export const partnerKennwortSchema = z.object({
    kontoId: z.string().uuid(),
    kennwort: z.string(),
});
/**
 * Was sich an einem Kunden ändern lässt.
 *
 * Alle vier wahlfrei, mindestens eines muss dabei sein — eine Änderung, die
 * nichts ändert, ist ein Programmierfehler und keine gültige Anfrage.
 *
 * Welches Recht welches Feld verlangt, entscheidet der Server
 * (`partner/kundeAendern.ts`). Der Vertrag sagt nur, welche Werte überhaupt
 * zulässig sind.
 */
export const partnerKundeAendernSchema = z
    .object({
    /** `null` heißt ausdrücklich „kein Cap" — nicht „nicht angegeben". */
    seatCap: z.number().int().nonnegative().max(100_000).nullable().optional(),
    adminLightStufe: z.enum(adminLightStufen).optional(),
    zweiFaktorPflicht: z.boolean().optional(),
    /** Plattform-Standard zwei; darüber hinaus bis zehn, dann ist es ein Tippfehler. */
    geraeteJeAnwender: z.number().int().min(1).max(10).optional(),
})
    .refine((wert) => Object.values(wert).some((v) => v !== undefined), {
    message: 'Mindestens ein Feld muss angegeben sein',
});
/**
 * Eine Änderung im Nachweis.
 *
 * `vorher` und `nachher` sind Text, obwohl die Felder verschiedene Typen
 * haben: Die Frage ist immer dieselbe — von was auf was? `null` heißt „war
 * nicht gesetzt" und steht in der Anzeige als „kein Cap".
 */
export const partnerKundeAenderungSchema = z.object({
    feld: z.string(),
    vorher: z.string().nullable(),
    nachher: z.string().nullable(),
    wann: z.string(),
    wer: z.string(),
});
export const partnerKundeAenderungenSchema = z.object({
    aenderungen: z.array(partnerKundeAenderungSchema),
});
/**
 * Ein Kunde, den der Partner beantragt.
 *
 * Der Auftrag geht in die Warteschlange, nicht in die Tat: Datenbank und Rolle
 * legt der Bereitstellungsarbeiter an, ein eigener Prozess mit eigener Rolle
 * (securitytodo.md ST-027). Der Prozess, der diese Anfrage bedient, hat kein
 * Recht dazu — und soll es nicht haben.
 */
export const partnerKundeBeantragenSchema = z.object({
    /** Vollständiger Hostname, unter dem der Kunde erreichbar sein wird. */
    host: z
        .string()
        .trim()
        .min(4)
        .max(253)
        .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/, {
        message: 'Kein gültiger Hostname',
    }),
    anzeigename: z.string().trim().min(1).max(120),
    edition: editionSchluessel,
    seatCap: z.number().int().nonnegative().max(100_000).nullable(),
    istDemo: z.boolean(),
});
/** Zustände eines Bereitstellungsauftrags. */
export const auftragZustaende = [
    'wartet_auf_freigabe',
    'freigegeben',
    'laeuft',
    'fertig',
    'fehlgeschlagen',
    'zurueckgezogen',
];
/**
 * Ein Auftrag in der Sicht des Partners.
 *
 * Ohne `payload`: Was drinsteht, sieht der Partner ohnehin am Kunden. Die
 * Rohdaten mitzuliefern hiesse, eine zweite Wahrheit zu pflegen.
 */
export const partnerAuftragSchema = z.object({
    id: z.string().uuid(),
    art: z.string(),
    zustand: z.enum(auftragZustaende),
    host: z.string(),
    anzeigename: z.string(),
    beantragtAm: z.string(),
    beantragtVon: z.string().nullable(),
    /** Grund bei Fehlschlag. `null`, solange nichts schiefging. */
    fehler: z.string().nullable(),
});
export const partnerAuftraegeSchema = z.object({
    auftraege: z.array(partnerAuftragSchema),
});
// ---------------------------------------------------------------------------
// Anrufauswertung (Edition Pro, ADR-0012)
// ---------------------------------------------------------------------------
/** Die wählbaren Zeiträume: laufende Woche, letzte n Tage, laufendes und vergangenes Jahr. */
export const auswertungZeitraeume = ['woche', '30', '90', '180', 'jahr', 'vorjahr'];
export const verteilungsklasseSchema = z.object({
    /** Beschriftung der Klasse, etwa `0–5 s`. */
    klasse: z.string(),
    anzahl: z.number().int().nonnegative(),
});
/** Wartezeiten: Kennzahlen und Verteilung — in Sekunden, `null` ohne Daten. */
export const wartezeitSchema = z.object({
    anzahl: z.number().int().nonnegative(),
    durchschnittSek: z.number().nullable(),
    medianSek: z.number().nullable(),
    p90Sek: z.number().nullable(),
    verteilung: z.array(verteilungsklasseSchema),
});
export const wochentagZeileSchema = z.object({
    /** 0 = Montag … 6 = Sonntag */
    wochentag: z.number().int().min(0).max(6),
    anzahl: z.number().int().nonnegative(),
});
export const stundeZeileSchema = z.object({
    stunde: z.number().int().min(0).max(23),
    anzahl: z.number().int().nonnegative(),
});
export const heatmapZelleSchema = z.object({
    wochentag: z.number().int().min(0).max(6),
    stunde: z.number().int().min(0).max(23),
    anzahl: z.number().int().nonnegative(),
});
/** Eingehende Anrufe von aussen — die Kennzahlen, die die Anlage und jeder Channel tragen. */
export const eingehendKennzahlenSchema = z.object({
    gesamt: z.number().int().nonnegative(),
    vonPersonAngenommen: z.number().int().nonnegative(),
    anrufbeantworter: z.number().int().nonnegative(),
    /** Aufgelegt, ohne dass eine Person abgehoben hat — Anrufbeantworter eingeschlossen. */
    nichtErreicht: z.number().int().nonnegative(),
    /** Vom Klingeln bis eine Person abhebt. */
    wartezeit: wartezeitSchema,
    /** Vom Klingeln bis der Anrufer aufgibt (auflegt oder beim Anrufbeantworter landet). */
    abbruch: wartezeitSchema,
});
/** Ein Mitarbeiter innerhalb eines Channels — was er dort angenommen und von dort gewählt hat. */
export const channelMitarbeiterSchema = z.object({
    userId: z.string(),
    name: z.string(),
    /** Auf diesem Channel von ihm angenommene Anrufe. */
    angenommen: z.number().int().nonnegative(),
    /** Über diesen Channel von ihm gewählte, zustande gekommene Gespräche. */
    ausgehend: z.number().int().nonnegative(),
    /** Summe der Gesprächsdauer in Sekunden, beide Richtungen. */
    dauerSek: z.number().int().nonnegative(),
});
/** Ein Team-Channel — die angerufene Nummer und was auf ihr eingeht. */
export const channelZeileSchema = z.object({
    /** Die angerufene Nummer, E.164 ohne Plus, wie sipgate sie schickt. */
    nummer: z.string(),
    /** Aus sipgate; `null`, solange der Channel nicht aufgelöst ist. */
    name: z.string().nullable(),
    anrufe: z.number().int().nonnegative(),
    vonPersonAngenommen: z.number().int().nonnegative(),
    wartezeitDurchschnittSek: z.number().nullable(),
    wartezeitMedianSek: z.number().nullable(),
    nichtErreicht: z.number().int().nonnegative(),
    /** 0–1 */
    nichtErreichtAnteil: z.number().min(0).max(1),
    abbruchDurchschnittSek: z.number().nullable(),
    /** Über diesen Channel gewählt (Push: `from` ist die Channel-Nummer). */
    ausgehend: z.object({
        gesamt: z.number().int().nonnegative(),
        zustandeGekommen: z.number().int().nonnegative(),
    }),
    /** Wie sich die Gespräche des Channels auf die Mitarbeiter verteilen — nach Gesprächen absteigend. */
    mitarbeiter: z.array(channelMitarbeiterSchema),
});
/** Ein Mitarbeiter — was er telefoniert hat und was auf seiner persönlichen Nummer einging. */
/** Wie ein einzelner Anruf ausging — für die letzten Gespräche eines Mitarbeiters. */
export const anrufAusgaenge = [
    /** Eingehend: eine Person hat abgehoben. */
    'angenommen',
    /** Eingehend: beim Anrufbeantworter gelandet. */
    'anrufbeantworter',
    /** Eingehend: der Anrufer hat vorher aufgelegt (`cancel`). */
    'abgebrochen',
    /** Ausgehend: die Gegenstelle hat abgehoben. */
    'zustande_gekommen',
    /** Nicht erreicht — besetzt, keine Antwort, sonstiger Grund in `grund`. */
    'nicht_erreicht',
    /** Läuft noch oder ohne Ende gemeldet. */
    'offen',
];
export const anrufKurzSchema = z.object({
    /** ISO-Zeitpunkt des Beginns (Klingeln beziehungsweise Wählen). */
    beginn: z.string(),
    /** Die Gegenstelle: eingehend der Anrufer, ausgehend das Ziel — `null`, wenn unbekannt oder anonym. */
    gegenstelle: z.string().nullable(),
    ausgang: z.enum(anrufAusgaenge),
    /** sipgate-Grund des Endes, etwa `busy`, `noAnswer`, `cancel`. */
    grund: z.string().nullable(),
    /** Gesprächsdauer nach dem Abheben; `null`, wenn kein Gespräch zustande kam. */
    dauerSek: z.number().int().nonnegative().nullable(),
});
export const mitarbeiterZeileSchema = z.object({
    userId: z.string(),
    name: z.string(),
    /** Angenommene eingehende Gespräche. */
    eingehend: z.number().int().nonnegative(),
    /** Zustande gekommene ausgehende Gespräche. */
    ausgehend: z.number().int().nonnegative(),
    gesamt: z.number().int().nonnegative(),
    /** Summe der Gesprächsdauer in Sekunden. */
    dauerSek: z.number().int().nonnegative(),
    /** Seine persönliche Nummer (persönlicher Channel bei sipgate), falls bekannt. */
    persoenlicheNummer: z.string().nullable(),
    /** Anrufe von aussen auf seine persönliche Nummer; `null` ohne bekannte Nummer. */
    aufNummer: z
        .object({
        anrufe: z.number().int().nonnegative(),
        vonPersonAngenommen: z.number().int().nonnegative(),
        nichtErreicht: z.number().int().nonnegative(),
        wartezeitMedianSek: z.number().nullable(),
        abbruchDurchschnittSek: z.number().nullable(),
    })
        .nullable(),
    /** Wann er telefoniert — seine Gespräche nach Wochentag und Stunde. */
    jeWochentag: z.array(wochentagZeileSchema),
    jeStunde: z.array(stundeZeileSchema),
    /** Die letzten drei eingehenden Anrufe an ihn (abgehoben oder auf seiner Nummer), neueste zuerst. */
    letzteEingehend: z.array(anrufKurzSchema).max(3),
    /** Die letzten drei ausgehenden Anrufe von ihm, neueste zuerst. */
    letzteAusgehend: z.array(anrufKurzSchema).max(3),
});
/**
 * Die Abschnitte der Anlagen-Ansicht — der Anwender ordnet sie selbst an
 * (GET/PUT `/api/auswertung/reihenfolge`). Die Namen sind Speicherformat:
 * umbenennen heißt migrieren.
 */
export const auswertungAbschnitte = [
    'erreichbarkeit',
    'wartezeiten',
    'stosszeiten',
    'heatmap',
    'gespraeche',
];
/** Jeder Abschnitt genau einmal — eine Permutation, keine Auswahl. */
export const auswertungReihenfolgeSchema = z.object({
    reihenfolge: z
        .array(z.enum(auswertungAbschnitte))
        .length(auswertungAbschnitte.length)
        .refine((liste) => new Set(liste).size === liste.length, 'Jeder Abschnitt genau einmal.'),
});
export const auswertungSchema = z.object({
    zeitraum: z.object({
        schluessel: z.enum(auswertungZeitraeume),
        von: z.string(),
        bis: z.string(),
    }),
    /** Die ganze Anlage. */
    anlage: z.object({
        eingehend: eingehendKennzahlenSchema,
        ausgehend: z.object({
            gesamt: z.number().int().nonnegative(),
            zustandeGekommen: z.number().int().nonnegative(),
        }),
        dauer: z.object({
            eingehendAnzahl: z.number().int().nonnegative(),
            ausgehendAnzahl: z.number().int().nonnegative(),
            eingehendSek: z.number().nullable(),
            ausgehendSek: z.number().nullable(),
            gesamtSek: z.number().nullable(),
        }),
        jeWochentag: z.array(wochentagZeileSchema),
        jeStunde: z.array(stundeZeileSchema),
        heatmap: z.array(heatmapZelleSchema),
    }),
    /** Nur Team-Channels — persönliche Nummern stehen beim Mitarbeiter. */
    channels: z.array(channelZeileSchema),
    mitarbeiter: z.array(mitarbeiterZeileSchema),
});
//# sourceMappingURL=contracts.js.map